import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Composant pour afficher un commentaire
 * Utilisé dans les listes de commentaires récents
 */
const CommentItem = ({ comment, compact = false }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));

    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    const diffHours = Math.ceil(diffMinutes / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    const diffDays = Math.ceil(diffHours / 24);
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString("fr-FR");
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span
          key={i}
          className={i < rating ? "text-yellow-400" : "text-white/40"}
        >
          ★
        </span>,
      );
    }
    return <div className="flex items-center text-sm">{stars}</div>;
  };

  const getInitials = () => {
    if (comment.first_name && comment.last_name) {
      return `${comment.first_name[0]}${comment.last_name[0]}`.toUpperCase();
    }
    return comment.username?.[0]?.toUpperCase() || "U";
  };

  if (compact) {
    return (
      <div
        onClick={() => navigate(`/recipe/${comment.recipe_id}`)}
        className="bg-white dark:bg-white/10 backdrop-blur-md rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-ink-900/10 dark:border-white/20 touch-manipulation"
      >
        <div className="flex items-start gap-2 sm:gap-3">
          {/* Avatar */}
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg sm:rounded-xl flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0">
            {comment.avatar_url ? (
              <img
                src={comment.avatar_url}
                alt={comment.username}
                className="w-full h-full rounded-lg sm:rounded-xl object-cover"
              />
            ) : (
              getInitials()
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-0.5 sm:mb-1">
              <span className="font-semibold text-xs sm:text-sm text-ink-900 dark:text-white truncate">
                {comment.first_name && comment.last_name
                  ? `${comment.first_name} ${comment.last_name}`
                  : comment.username}
              </span>
              <span className="text-[10px] sm:text-xs text-ink-500 dark:text-white/80">
                {formatDate(comment.created_at)}
              </span>
            </div>

            {comment.rating && renderStars(comment.rating)}

            <p className="text-xs sm:text-sm text-ink-600 dark:text-white/90 line-clamp-2 mt-0.5 sm:mt-1">
              {comment.comment_text}
            </p>

            {comment.recipe_title && (
              <p className="text-[10px] sm:text-xs text-orange-300 mt-1 sm:mt-2 font-medium truncate">
                Sur: {comment.recipe_title}
              </p>
            )}

            {comment.likes_count > 0 && (
              <div className="flex items-center gap-1 mt-1 sm:mt-2 text-[10px] sm:text-xs text-ink-500 dark:text-white/80">
                <span>❤️</span>
                <span>{comment.likes_count}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80">
      <div className="flex items-start space-x-4">
        {/* Avatar */}
        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
          {comment.avatar_url ? (
            <img
              src={comment.avatar_url}
              alt={comment.username}
              className="w-full h-full rounded-2xl object-cover"
            />
          ) : (
            getInitials()
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="font-semibold text-slate-800">
                {comment.first_name && comment.last_name
                  ? `${comment.first_name} ${comment.last_name}`
                  : comment.username}
              </h4>
              <p className="text-sm text-slate-500">
                {formatDate(comment.created_at)}
              </p>
            </div>

            {comment.rating && renderStars(comment.rating)}
          </div>

          <p className="text-slate-700 mb-3">{comment.comment_text}</p>

          {comment.recipe_title && (
            <div
              onClick={() => navigate(`/recipe/${comment.recipe_id}`)}
              className="inline-flex items-center space-x-2 px-3 py-1.5 bg-amber-50 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-100 transition-colors cursor-pointer"
            >
              <span>Voir la recette:</span>
              <span>{comment.recipe_title}</span>
            </div>
          )}

          {comment.likes_count > 0 && (
            <div className="flex items-center space-x-2 mt-3 text-sm text-slate-500">
              <span>❤️</span>
              <span>
                {comment.likes_count}{" "}
                {comment.likes_count > 1
                  ? "personnes ont aimé"
                  : "personne a aimé"}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
